import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  generateHexagramFromDate,
  hexagramChanged,
  hexagramMutual,
  Hexagram,
  yuelingWithElement,
} from '../core/meihua';
import { hexagramName } from '../core/gua64';
import {
  MEIHUA_CATEGORIES,
  MEIHUA_HORIZONS,
  MEIHUA_QUESTION_TIPS,
  MeihuaCategory,
  MeihuaHorizon,
  buildMeihuaRequest,
} from '../services/meihuaRequest';
import { saveChatSession, addFollowUpToChat } from '../services/historyService';
import { TiYongBoard } from '../components/TiYongChart';
import { AiResponseDisplay, AIResponse } from '../components/AiResponseDisplay';
import { ChatBubble } from '../components/ChatBubble';
import { PageHeader } from '../components/PageHeader';
import { smoothScrollToElement } from '../utils/smoothScroll';
import { useBilling } from '../hooks/useBilling';

type TimeFields = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
};

const pad2 = (n: number): string => n.toString().padStart(2, '0');

const fieldsFromDate = (date: Date): TimeFields => ({
  year: String(date.getFullYear()),
  month: pad2(date.getMonth() + 1),
  day: pad2(date.getDate()),
  hour: pad2(date.getHours()),
  minute: pad2(date.getMinutes()),
});

/** 把五组输入解析成真实日期；非法（如 2 月 31 日）返回 null。 */
const dateFromFields = (fields: TimeFields): Date | null => {
  const year = Number(fields.year);
  const month = Number(fields.month);
  const day = Number(fields.day);
  const hour = Number(fields.hour);
  const minute = Number(fields.minute);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
  if (year < 1900 || year > 2100) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatChartTime = (date: Date): string => {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/** 起卦结果是否相同：本卦上下卦与动爻一致即可（互 / 变皆由本卦推出）。 */
const sameHexagram = (a: Hexagram, b: Hexagram): boolean =>
  a.upper === b.upper && a.lower === b.lower && a.movingLine === b.movingLine;

/** 把后端的 JSON / HTML 错误收敛成一句用户能看懂的话。 */
const parseApiError = async (response: Response): Promise<string> => {
  const text = await response.text();
  let message = text;
  try {
    const data = JSON.parse(text);
    message = data?.message || data?.error || text;
  } catch {
    /* 非 JSON 响应，保留原文 */
  }
  if (typeof message !== 'string') message = text;
  if (/validate JSON|failed_generation|json_validate_failed|invalid response format/i.test(message)) {
    return '本次解读没有生成完整结果，请重试。';
  }
  if (/rate limit|too many requests|\b429\b|\bTPM\b/i.test(message)) {
    return '当前使用人数较多，请稍等片刻再试。';
  }
  if (/service configuration/i.test(message)) return 'AI 服务暂时不可用，请稍后再试。';
  if (/quota|insufficient|额度不足/i.test(message)) return 'AI 解读额度不足，请升级后继续。';
  const trimmed = message.trim();
  if (!trimmed) return `请求失败（${response.status}）`;
  return trimmed.length > 160 ? `${trimmed.slice(0, 160)}…` : trimmed;
};

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3.2 13.9 9l5.8 1.9-5.8 1.9L12 18.6l-1.9-5.8L4.3 10.9 10.1 9 12 3.2Z" />
    <path d="M18.6 16.2l.8 2.4 2.4.8-2.4.8-.8 2.4-.8-2.4-2.4-.8 2.4-.8.8-2.4Z" opacity=".7" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 19V5M5.5 11.5 12 5l6.5 6.5" />
  </svg>
);

export const MeihuaPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { balance, refresh: refreshCredits } = useBilling();

  const [question, setQuestion] = useState('');
  const [useNow, setUseNow] = useState(true);
  const [timeFields, setTimeFields] = useState<TimeFields>(() => fieldsFromDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResponse, setAiResponse] = useState<AIResponse | null>(null);
  const [streamReading, setStreamReading] = useState(false);
  const [streamFollowUpIndex, setStreamFollowUpIndex] = useState<number | null>(null);
  const [hexagrams, setHexagrams] = useState<{
    original: Hexagram;
    mutual: Hexagram;
    changed: Hexagram;
    yueling: string;
  } | null>(null);
  const [chartTime, setChartTime] = useState<string>('');
  const [chartDate, setChartDate] = useState<Date | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [chatId, setChatId] = useState<string | null>(null);
  const [firestoreId, setFirestoreId] = useState<string | null>(null);
  const [followUpHistory, setFollowUpHistory] = useState<{ question: string; answer: string }[]>([]);
  const [category, setCategory] = useState<MeihuaCategory>('general');
  const [horizon, setHorizon] = useState<MeihuaHorizon>('mid');
  const [background, setBackground] = useState('');
  const [showTips, setShowTips] = useState(false);
  const [outOfCredits, setOutOfCredits] = useState(false);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('meihua_ai_session');
      if (!saved) return;
      const data = JSON.parse(saved);
      if (data.question) setQuestion(data.question);
      if (data.useNow !== undefined) setUseNow(data.useNow);
      if (data.timeFields) setTimeFields(data.timeFields);
      else if (data.timeValue) {
        const legacy = new Date(data.timeValue);
        if (!isNaN(legacy.getTime())) setTimeFields(fieldsFromDate(legacy));
      }
      if (data.chartDate) {
        const d = new Date(data.chartDate);
        if (!isNaN(d.getTime())) {
          setChartDate(d);
          setChartTime(formatChartTime(d));
        }
      } else if (data.chartTime) {
        setChartTime(data.chartTime);
      }
      if (data.hexagrams) setHexagrams(data.hexagrams);
      if (data.aiResponse) setAiResponse(data.aiResponse);
      setStreamReading(false);
      setStreamFollowUpIndex(null);
      if (data.chatId) setChatId(data.chatId);
      if (data.firestoreId) setFirestoreId(data.firestoreId);
      if (data.followUpHistory) setFollowUpHistory(data.followUpHistory);
      if (data.category) setCategory(data.category);
      if (data.horizon) setHorizon(data.horizon);
      if (data.background) setBackground(data.background);
    } catch (e) {
      console.error('Failed to restore session', e);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      'meihua_ai_session',
      JSON.stringify({
        question,
        useNow,
        timeFields,
        chartDate: chartDate ? chartDate.toISOString() : null,
        hexagrams,
        chartTime,
        aiResponse,
        chatId,
        firestoreId,
        followUpHistory,
        category,
        horizon,
        background,
      })
    );
  }, [
    question,
    useNow,
    timeFields,
    chartDate,
    hexagrams,
    chartTime,
    aiResponse,
    chatId,
    firestoreId,
    followUpHistory,
    category,
    horizon,
    background,
  ]);

  const apiBase = useMemo(() => {
    const envBase = (import.meta as any).env?.VITE_API_BASE as string | undefined;
    if (envBase && envBase.trim().length > 0) return envBase.replace(/\/+$/, '');
    return 'https://us-central1-meihua-16427.cloudfunctions.net';
  }, []);

  /** 当前应使用的起卦时刻：跟随「当前时刻」或五个指定时间输入。 */
  const resolveTimeDate = (): Date => {
    if (useNow) return new Date();
    return dateFromFields(timeFields) || new Date();
  };

  const buildChart = (date: Date = resolveTimeDate()) => {
    const original = generateHexagramFromDate(date);
    return {
      date,
      chart: {
        original,
        mutual: hexagramMutual(original),
        changed: hexagramChanged(original),
        yueling: yuelingWithElement(date),
      },
    };
  };

  /** 用指定时刻重排盘面；盘面变了，旧解读与追问一并失效。 */
  const applyChart = (date: Date, clearReading = true) => {
    const { chart } = buildChart(date);
    setHexagrams(chart);
    setChartTime(formatChartTime(date));
    setChartDate(date);
    if (clearReading) {
      setAiResponse(null);
      setStreamReading(false);
      setStreamFollowUpIndex(null);
      setFollowUpHistory([]);
      setChatId(null);
      setFirestoreId(null);
      setAiError(null);
      setOutOfCredits(false);
    }
  };

  /**
   * 同一时辰内分钟变化不改变起卦结果：只更新时间显示，保留已有解读。
   * 只有本卦（上下卦 / 动爻）真的变了，才重排并作废旧解读。
   */
  const refreshChart = (date: Date) => {
    const next = generateHexagramFromDate(date);
    if (hexagrams && sameHexagram(hexagrams.original, next)) {
      setChartTime(formatChartTime(date));
      setChartDate(date);
      return;
    }
    applyChart(date);
  };

  /** 指定时间输入：只在日期合法时实时重排，避免打到一半盘面乱跳。 */
  const updateTimeField = (key: keyof TimeFields, raw: string) => {
    const digits = raw.replace(/[^\d]/g, '').slice(0, key === 'year' ? 4 : 2);
    const next = { ...timeFields, [key]: digits };
    setTimeFields(next);
    if (!useNow && hexagrams) {
      const date = dateFromFields(next);
      if (date) refreshChart(date);
    }
  };

  const chooseNow = () => {
    const now = new Date();
    setUseNow(true);
    setTimeFields(fieldsFromDate(now));
    if (hexagrams) refreshChart(now);
  };

  const chooseCustomTime = () => {
    setUseNow(false);
    const date = dateFromFields(timeFields);
    if (hexagrams && date) refreshChart(date);
  };

  const scrollToResult = () => {
    window.setTimeout(() => {
      const el = document.getElementById('meihua-result');
      if (el) smoothScrollToElement(el);
    }, 80);
  };

  const handleReset = () => {
    if (aiResponse && !confirm('确定要清空当前解读并开始新的提问吗？')) return;
    sessionStorage.removeItem('meihua_ai_session');
    setQuestion('');
    setAiResponse(null);
    setStreamReading(false);
    setStreamFollowUpIndex(null);
    setHexagrams(null);
    setChartTime('');
    setChartDate(null);
    setFollowUpHistory([]);
    setChatId(null);
    setFirestoreId(null);
    setAiError(null);
    setOutOfCredits(false);
    setBackground('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChartOnly = () => {
    setAiError(null);
    setOutOfCredits(false);
    if (!useNow && !dateFromFields(timeFields)) {
      setAiError('起卦时间不完整或不存在，请检查年、月、日、时、分。');
      return;
    }
    setAiResponse(null);
    setStreamReading(false);
    setStreamFollowUpIndex(null);
    setFollowUpHistory([]);
    setChatId(null);
    setFirestoreId(null);
    const { date, chart } = buildChart();
    setHexagrams(chart);
    setChartTime(formatChartTime(date));
    setChartDate(date);
    scrollToResult();
  };

  const handleAsk = async () => {
    setAiError(null);
    setOutOfCredits(false);
    if (!useNow && !dateFromFields(timeFields)) {
      setAiError('起卦时间不完整或不存在，请检查年、月、日、时、分。');
      return;
    }
    setAiResponse(null);
    setStreamReading(false);
    setStreamFollowUpIndex(null);
    setFollowUpHistory([]);
    setFirestoreId(null);
    const trimmed = question.trim();
    if (!trimmed) {
      setAiError('请先写下想问的事。');
      return;
    }
    const { date, chart } = buildChart();
    setHexagrams(chart);
    setChartTime(formatChartTime(date));
    setChartDate(date);
    try {
      setLoading(true);
      if (!user) throw new Error('用户未登录');
      const idToken = await user.getIdToken();
      const requestBody = buildMeihuaRequest({
        question: trimmed,
        original: chart.original,
        mutual: chart.mutual,
        changed: chart.changed,
        date,
        category,
        horizon,
        background,
      });

      const response = await fetch(`${apiBase}/api/ai/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 402) {
        const payload = await response.json().catch(() => ({}));
        setOutOfCredits(true);
        setAiError(payload?.message || 'AI 解读次数已用完，升级基础版即可继续。');
        void refreshCredits();
        return;
      }
      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }
      const data = await response.json();
      if (!data.success || !data.data) throw new Error('AI 返回格式不正确');

      const result: AIResponse = data.data;
      const newChatId = data.metadata?.chatId || null;
      setChatId(newChatId);
      setAiResponse(result);
      setStreamReading(true);
      void refreshCredits();

      if (newChatId) {
        setFirestoreId(newChatId);
      } else {
        try {
          const newFirestoreId = await saveChatSession(user.uid, trimmed, requestBody.gua, result);
          setFirestoreId(newFirestoreId);
        } catch (saveError: any) {
          console.error('Failed to save chat history:', saveError);
          setAiError(`解读已完成，但历史记录保存失败（${saveError.message}）。`);
        }
      }
    } catch (err: any) {
      setAiError(err?.message || '请求失败，请稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async () => {
    const trimmed = followUpQuestion.trim();
    if (!trimmed) return;
    if (!chatId && !firestoreId) {
      setAiError('无法找到对话 ID，请重新提问。');
      return;
    }
    if (!hexagrams || !aiResponse) {
      setAiError('缺少对话上下文，请重新提问。');
      return;
    }
    try {
      setLoading(true);
      setAiError(null);
      if (!user) throw new Error('用户未登录');
      const idToken = await user.getIdToken();
      const effectiveChatId = chatId || firestoreId || 'temp-id';

      const response = await fetch(`${apiBase}/api/ai/ask-followup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          newQuestion: trimmed,
          previousQuestion: question,
          previousAnswer: aiResponse,
          gua: buildMeihuaRequest({
            question,
            original: hexagrams.original,
            mutual: hexagrams.mutual,
            changed: hexagrams.changed,
            date: chartDate || resolveTimeDate(),
            category,
            horizon,
            background,
          }).gua,
          category,
          horizon,
          chatId: effectiveChatId,
        }),
      });

      if (response.status === 402) {
        const payload = await response.json().catch(() => ({}));
        setOutOfCredits(true);
        setAiError(payload?.message || 'AI 解读次数已用完，升级基础版即可继续。');
        void refreshCredits();
        return;
      }
      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }
      const data = await response.json();
      if (!data.success || !data.data) throw new Error('AI 返回格式不正确');

      const followUpResult = data.data;
      let answerText = '';
      if (followUpResult.summary) {
        answerText = followUpResult.summary;
      } else if (followUpResult.contextAcknowledgment && followUpResult.practicalGuidance) {
        answerText = `${followUpResult.contextAcknowledgment}\n\n${followUpResult.practicalGuidance}`;
      } else {
        answerText = JSON.stringify(followUpResult, null, 2);
      }

      const nextFollowUpIndex = followUpHistory.length;
      setFollowUpHistory((prev) => [...prev, { question: trimmed, answer: answerText }]);
      setStreamFollowUpIndex(nextFollowUpIndex);
      setFollowUpQuestion('');
      void refreshCredits();

      const backendPersistedFollowUp = Boolean(chatId || data.metadata?.provider);
      if (!backendPersistedFollowUp && firestoreId) {
        try {
          await addFollowUpToChat(user.uid, firestoreId, trimmed, followUpResult || answerText);
        } catch (saveError) {
          console.error('Failed to save follow-up:', saveError);
        }
      }
    } catch (err: any) {
      setAiError(err?.message || '请求失败，请稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  const activeCategory = MEIHUA_CATEGORIES.find((c) => c.key === category);
  const hasChart = Boolean(hexagrams);
  const resultTitle = hexagrams
    ? hexagrams.original.movingLine
      ? `${hexagramName(hexagrams.original)} 之 ${hexagramName(hexagrams.changed)}`
      : `${hexagramName(hexagrams.original)} · 静卦`
    : '';

  return (
    <div className="page">
      <PageHeader
        eyebrow="梅花易数 · 一事一占"
        title="问事解卦"
        subtitle="把问题说清楚，卦由起卦时刻生成。AI 依体用、旺衰、互变逐层解读，并给出可执行的建议。"
        actions={
          (hasChart || aiResponse) && (
            <button className="text-btn" onClick={handleReset}>
              新占
            </button>
          )
        }
      />

      <section className="composer">
        <div className="composer-head">
          <label className="field-label" htmlFor="meihua-question">所问何事</label>
          <span className="char-count">{question.length}/160</span>
        </div>
        <textarea
          id="meihua-question"
          value={question}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
          rows={3}
          maxLength={160}
          className="ask-input"
          placeholder="例：三个月内能否拿到心仪公司的 offer？"
        />

        <div className="field-label-row">
          <span className="field-label">问事类别</span>
          <button type="button" className="link-btn" onClick={() => setShowTips((v) => !v)}>
            {showTips ? '收起写法' : '怎么问更准？'}
          </button>
        </div>

        <div className="chip-scroller" role="group" aria-label="问事类别">
          {MEIHUA_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              title={c.hint}
              aria-pressed={category === c.key}
              onClick={() => setCategory(c.key)}
              className={`chip ${category === c.key ? 'is-active' : ''}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {activeCategory && <p className="field-hint">{activeCategory.hint}</p>}

        {showTips && (
          <ul className="tip-list">
            {MEIHUA_QUESTION_TIPS.map((t) => <li key={t}>{t}</li>)}
          </ul>
        )}

        <div className="field-block time-block">
          <div className="field-label-row">
            <span className="field-label">起卦时间</span>
            <span className="field-optional">{useNow ? '默认取当前时刻' : '按指定时刻起卦'}</span>
          </div>
          <div className="segmented" role="group" aria-label="起卦时间">
            <button type="button" aria-pressed={useNow} onClick={chooseNow} className={useNow ? 'is-active' : ''}>
              当前时刻
            </button>
            <button type="button" aria-pressed={!useNow} onClick={chooseCustomTime} className={!useNow ? 'is-active' : ''}>
              指定时间
            </button>
          </div>
          {!useNow && (
            <>
              <div className="time-fields">
                {([
                  ['year', '年', 1900, 2100],
                  ['month', '月', 1, 12],
                  ['day', '日', 1, 31],
                  ['hour', '时', 0, 23],
                  ['minute', '分', 0, 59],
                ] as const).map(([key, label, min, max]) => (
                  <label key={key}>
                    <span>{label}</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={min}
                      max={max}
                      value={timeFields[key]}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateTimeField(key, e.target.value)}
                    />
                  </label>
                ))}
              </div>
              {dateFromFields(timeFields) ? (
                <p className="field-hint">
                  盘面按 <b>{formatChartTime(dateFromFields(timeFields)!)}</b> 起卦
                  {hexagrams ? '（已同步重排）' : '（排盘后生效）'}。
                </p>
              ) : (
                <p className="field-hint is-warn">日期或时间不完整，盘面暂不更新。</p>
              )}
            </>
          )}
        </div>

        <details className="advanced">
          <summary>
            <span>应期与背景</span>
            <span className="advanced-state">可选</span>
          </summary>
          <div className="advanced-body">
            <div className="field-block">
              <span className="field-label">应期范围</span>
              <div className="segmented" role="group" aria-label="应期范围">
                {MEIHUA_HORIZONS.map((h) => (
                  <button
                    key={h.key}
                    type="button"
                    aria-pressed={horizon === h.key}
                    onClick={() => setHorizon(h.key)}
                    className={horizon === h.key ? 'is-active' : ''}
                  >
                    {h.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field-block">
              <span className="field-label">背景补充 <span className="field-optional">仅作语境参考</span></span>
              <textarea
                value={background}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBackground(e.target.value)}
                rows={2}
                maxLength={200}
                className="mei-input"
                placeholder="例：现职做了两年，最近在面试两家公司。"
              />
            </div>
          </div>
        </details>

        <div className="composer-actions">
          <button type="button" className="mei-ghost-btn" onClick={handleChartOnly} disabled={loading}>
            {hasChart ? '重新排盘' : '只看卦象'}
          </button>
          <button type="button" className="mei-primary-btn" onClick={handleAsk} disabled={loading}>
            {loading ? <span className="mei-spinner" /> : <SparkIcon />}
            {loading ? '解读中…' : aiResponse ? '重新解读' : 'AI 解读'}
          </button>
        </div>
        <p className="composer-foot">
          排盘免费 · 本次 AI 解读消耗 1 次额度
          {balance !== null && <> · 剩余 <b>{balance}</b> 次</>}
        </p>

        {aiError && <div className="mei-error">{aiError}</div>}

        {outOfCredits && (
          <div className="upgrade-card">
            <div>
              <div className="upgrade-title">升级基础版，继续解读</div>
              <p className="upgrade-sub">每月 200 次 AI 解读，追问不另计。排盘始终免费。</p>
            </div>
            <button className="mei-primary-btn" onClick={() => navigate('/app/pricing')}>
              查看订阅计划
            </button>
          </div>
        )}
      </section>

      {!hasChart && !loading && (
        <section className="guide-card">
          <div className="guide-head">
            <span className="field-label">如何得到一次有用的解读</span>
            <button className="link-btn" onClick={() => navigate('/app/history')}>查看记录 →</button>
          </div>
          <ol className="guide-steps">
            <li>
              <span className="guide-num">壹</span>
              <div><b>问一件具体的事</b><span>带上对象与时间范围，比「我最近怎么样」更容易断。</span></div>
            </li>
            <li>
              <span className="guide-num">贰</span>
              <div><b>选对问事类别</b><span>类别决定取象侧重，不是给答案贴标签。</span></div>
            </li>
            <li>
              <span className="guide-num">叁</span>
              <div><b>先看盘，再读解</b><span>体用与旺衰是依据，AI 只负责把依据讲清楚。</span></div>
            </li>
          </ol>
        </section>
      )}

      {hasChart && hexagrams && (
        <section className="result-card" id="meihua-result">
          <div className="result-head">
            <div>
              <div className="result-eyebrow">卦象已成</div>
              <h2 className="result-title">{resultTitle}</h2>
              <div className="result-meta">
                <span>{chartTime || formatChartTime(new Date())}</span>
                <span className="dot-sep" />
                <span>{hexagrams.yueling}</span>
                {activeCategory && <><span className="dot-sep" /><span>{activeCategory.label}</span></>}
              </div>
            </div>
            <button className="icon-btn" onClick={handleChartOnly} title="重新排盘" aria-label="重新排盘">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 11a8 8 0 1 0-2.3 6.3M20 4v7h-7" />
              </svg>
            </button>
          </div>
          <TiYongBoard
            original={hexagrams.original}
            mutual={hexagrams.mutual}
            changed={hexagrams.changed}
            yueling={hexagrams.yueling}
          />
        </section>
      )}

      {loading && (
        <section className="mei-card loading-card">
          <div className="mei-spinner" />
          <div>
            <div className="loading-title">{aiResponse ? '正在追问' : '正在按梅花次第解读'}</div>
            <div className="mei-muted">体用 → 旺衰 → 互变，逐层取象</div>
          </div>
        </section>
      )}

      {aiResponse && (
        <AiResponseDisplay response={aiResponse} animate={streamReading} />
      )}

      {followUpHistory.length > 0 && (
        <section className="followup-history">
          {followUpHistory.map((item, index) => (
            <div key={index}>
              <ChatBubble role="user" content={item.question} />
              <ChatBubble
                role="assistant"
                content={item.answer}
                animate={streamFollowUpIndex === index}
              />
            </div>
          ))}
        </section>
      )}

      {aiResponse && (
        <div className="followup-wrap">
          <form
            className="followup"
            onSubmit={(e) => {
              e.preventDefault();
              void handleFollowUp();
            }}
          >
            <input
              value={followUpQuestion}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFollowUpQuestion(e.target.value)}
              placeholder="就这一卦继续追问…"
              aria-label="继续追问"
            />
            <button type="submit" disabled={loading || !followUpQuestion.trim()} aria-label="发送追问">
              <ArrowIcon />
            </button>
          </form>
          <p className="followup-hint">追问沿用同一卦盘，不重复起卦</p>
        </div>
      )}
    </div>
  );
};
